import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, ArrowLeft, MapPin, Car, Calendar, Mail, Phone, CheckCircle2, Lock, Loader2, DollarSign, Gauge, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { VEHICLE_TYPES } from '../mock';
import { trackQuoteSubmitted } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteCalculator = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [trackingCode, setTrackingCode] = useState('');
  const [form, setForm] = useState({
    from: '',
    to: '',
    trailer: 'open',
    vehicleType: '',
    condition: 'running',
    name: '',
    email: '',
    phone: '',
    date: '',
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const fetchEstimate = async () => {
    setEstimateLoading(true);
    try {
      const { data } = await axios.post(`${API}/pricing/estimate`, {
        fromLocation: form.from,
        toLocation: form.to,
        trailer: form.trailer,
        vehicleType: form.vehicleType,
        condition: form.condition,
      });
      setEstimate(data);
    } catch (err) {
      console.error(err);
      toast({ title: 'Could not calculate estimate', description: 'You can still submit your details — our team will contact you.' });
    } finally {
      setEstimateLoading(false);
    }
  };

  const next = async () => {
    if (step === 1) {
      if (!form.from || !form.to) return toast({ title: 'Please complete all fields', description: 'Enter both pickup and delivery locations.' });
    }
    if (step === 2) {
      if (!form.vehicleType) return toast({ title: 'Select a vehicle type' });
      // Moving to Estimate step → compute it now
      await fetchEstimate();
    }
    setStep(s => Math.min(4, s + 1));
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      return toast({ title: 'Please fill all contact fields' });
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, {
        fromLocation: form.from,
        toLocation: form.to,
        trailer: form.trailer,
        vehicleType: form.vehicleType,
        condition: form.condition,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: form.date || null,
      });
      setTrackingCode(data?.trackingCode || '');
      setDone(true);
      trackQuoteSubmitted({ value: estimate?.totalMax || estimate?.totalMin || 1 });
      toast({ title: 'Quote request received!', description: "We'll contact you within 15 minutes." });
    } catch (err) {
      console.error(err);
      toast({ title: 'Could not submit', description: err?.response?.data?.detail || 'Please try again or call us directly.' });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 border border-[#E5EAF2]" data-testid="quote-success-card">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-9 h-9 text-green-600" />
          </div>
          <h3 className="text-2xl font-extrabold text-[#0A1628] mb-2">You're all set, {form.name.split(' ')[0] || 'friend'}!</h3>
          <p className="text-[#4A5A72] mb-6 text-sm">Our dispatch team will reach out at <strong>{form.phone}</strong> within 15 minutes with your final broker-verified quote.</p>

          {estimate && (
            <div data-testid="quote-done-estimate" className="w-full bg-gradient-to-br from-[#0A1628] to-[#0E2744] rounded-2xl p-5 text-left mb-4 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#D4A24C]/20 blur-2xl" />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#2AA8E8] mb-1.5">Estimated price</div>
                <div className="text-3xl font-extrabold gold-text leading-none">${estimate.totalMin}<span className="text-white/60 text-2xl mx-1">–</span>${estimate.totalMax}</div>

                <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-white/15">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Distance</div>
                    <div className="text-sm font-extrabold text-white mt-0.5">{estimate.distanceMiles} mi</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Transit</div>
                    <div className="text-sm font-extrabold text-white mt-0.5">{estimate.transitWindow || `${estimate.transitDaysMin}–${estimate.transitDaysMax} days`}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">Type</div>
                    <div className="text-sm font-extrabold text-white mt-0.5 capitalize">{estimate.trailer}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="w-full bg-[#F7F9FC] rounded-xl p-4 text-left text-xs mb-5">
            <div className="flex justify-between py-0.5"><span className="text-[#4A5A72]">From</span><strong>{form.from}</strong></div>
            <div className="flex justify-between py-0.5"><span className="text-[#4A5A72]">To</span><strong>{form.to}</strong></div>
            <div className="flex justify-between py-0.5"><span className="text-[#4A5A72]">Vehicle</span><strong>{form.vehicleType}</strong></div>
            <div className="flex justify-between py-0.5"><span className="text-[#4A5A72]">Condition</span><strong className="capitalize">{form.condition}</strong></div>
          </div>

          {trackingCode && (
            <div
              data-testid="quote-tracking-code"
              className="w-full bg-gradient-to-r from-[#D4A24C]/10 to-[#2AA8E8]/10 border border-[#D4A24C]/40 rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3"
            >
              <div className="text-left">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#4A5A72]">Your reference code</div>
                <div className="text-lg font-extrabold text-[#0A1628] tracking-wider">{trackingCode}</div>
              </div>
              <div className="text-[10px] font-semibold text-[#4A5A72] text-right">Keep this code<br/>for your records</div>
            </div>
          )}

          {/* 3 CTAs */}
          <div className="w-full grid grid-cols-1 gap-2">
            <Button data-testid="quote-book-now-btn" onClick={() => window.location.assign(`/contact?quote=1&from=${encodeURIComponent(form.from)}&to=${encodeURIComponent(form.to)}`)} className="btn-accent h-11 font-bold gap-2">
              Book Now <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="quote-broker-btn" variant="outline" onClick={() => window.location.assign('/contact')} className="h-11 font-semibold border-[#0A1628]/20 text-[#0A1628] hover:bg-[#0A1628] hover:text-white gap-2">
              Request Final Broker Quote
            </Button>
            <a href="tel:+17184904076" data-testid="quote-call-btn" className="h-11 inline-flex items-center justify-center gap-2 rounded-md font-bold text-[#0A1628] border border-[#D4A24C] hover:bg-[#D4A24C] transition-colors">
              <Phone className="w-4 h-4" /> Call Now: (718) 490-4076
            </a>
          </div>

          <button onClick={() => { setDone(false); setStep(1); setEstimate(null); setTrackingCode(''); setForm({ from:'', to:'', trailer:'open', vehicleType:'', condition:'running', name:'', email:'', phone:'', date:'' }); }} className="mt-5 text-xs font-semibold text-[#4A5A72] underline underline-offset-4 hover:text-[#D4A24C]">Request another quote</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-[#E5EAF2] overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-[#F0F3F8]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-extrabold text-[#0A1628]">Get a Quote</h3>
          <span className="text-xs font-semibold text-[#4A5A72]">Takes 10 seconds</span>
        </div>
        {/* Progress */}
        <div className="flex items-center gap-2 mt-3">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className={`flex-1 h-1.5 rounded-full transition-colors ${step >= n ? 'bg-[#D4A24C]' : 'bg-[#E5EAF2]'}`} />
          ))}
        </div>
        <div className="flex justify-between text-[11px] font-semibold uppercase tracking-wider text-[#4A5A72] mt-2">
          <span className={step >= 1 ? 'text-[#0A1628]' : ''}>Route</span>
          <span className={step >= 2 ? 'text-[#0A1628]' : ''}>Vehicle</span>
          <span className={step >= 3 ? 'text-[#0A1628]' : ''}>Estimate</span>
          <span className={step >= 4 ? 'text-[#0A1628]' : ''}>Contact</span>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 space-y-4">
        {step === 1 && (
          <>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Ship FROM</Label>
              <div className="relative mt-1.5">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input value={form.from} onChange={e => update('from', e.target.value)} placeholder="City, State or ZIP" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Ship TO</Label>
              <div className="relative mt-1.5">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input value={form.to} onChange={e => update('to', e.target.value)} placeholder="City, State or ZIP" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Transport Type</Label>
              <RadioGroup value={form.trailer} onValueChange={v => update('trailer', v)} className="grid grid-cols-2 gap-2 mt-1.5">
                {['open', 'enclosed'].map(t => (
                  <label key={t} className={`border rounded-lg px-3 py-2.5 cursor-pointer flex items-center gap-2 transition-colors ${form.trailer === t ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                    <RadioGroupItem value={t} id={`tr-${t}`} />
                    <span className="capitalize font-semibold text-sm text-[#0A1628]">{t}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Vehicle Type</Label>
              <Select value={form.vehicleType} onValueChange={v => update('vehicleType', v)}>
                <SelectTrigger className="mt-1.5 h-11">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#4A5A72]" />
                    <SelectValue placeholder="Select vehicle type" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Vehicle Condition</Label>
              <RadioGroup value={form.condition} onValueChange={v => update('condition', v)} className="grid grid-cols-2 gap-2 mt-1.5">
                {[['running', 'Running'], ['non-running', 'Non-running']].map(([v, l]) => (
                  <label key={v} className={`border rounded-lg px-3 py-2.5 cursor-pointer flex items-center gap-2 transition-colors ${form.condition === v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                    <RadioGroupItem value={v} id={`cd-${v}`} />
                    <span className="font-semibold text-sm text-[#0A1628]">{l}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Ready for Pickup</Label>
              <div className="relative mt-1.5">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input type="date" value={form.date} onChange={e => update('date', e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <div data-testid="quote-estimate-step" className="space-y-4">
            {estimateLoading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-[#2AA8E8] mb-3" />
                <p className="text-sm text-[#4A5A72]">Crunching the numbers…</p>
              </div>
            ) : estimate ? (
              <>
                <div className="rounded-xl overflow-hidden border border-[#E5EAF2]">
                  <div className="bg-gradient-to-br from-[#0A1628] to-[#0E2744] p-5 text-white">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#2AA8E8] mb-1">
                      <DollarSign className="w-3.5 h-3.5" /> Your instant estimate
                    </div>
                    <div data-testid="quote-estimate-total" className="text-3xl font-extrabold gold-text leading-none mt-1">
                      ${estimate.totalMin}<span className="text-white/50 text-2xl"> – </span>${estimate.totalMax}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/70 mt-2">
                      <span className="inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" /> ~{estimate.distanceMiles} mi</span>
                      <span className="w-1 h-1 rounded-full bg-white/30" />
                      <span>{estimate.transitWindow || `${estimate.transitDaysMin}–${estimate.transitDaysMax} days`}</span>
                    </div>
                  </div>
                  <div className="bg-white p-4 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-[#4A5A72] font-semibold uppercase tracking-wider text-[10px]">Distance</div>
                      <div className="text-[#0A1628] font-bold">{estimate.distanceMiles} mi</div>
                    </div>
                    <div>
                      <div className="text-[#4A5A72] font-semibold uppercase tracking-wider text-[10px]">Transit time</div>
                      <div className="text-[#0A1628] font-bold">{estimate.transitWindow || `${estimate.transitDaysMin}–${estimate.transitDaysMax} days`}</div>
                    </div>
                    <div>
                      <div className="text-[#4A5A72] font-semibold uppercase tracking-wider text-[10px]">Trailer</div>
                      <div className="text-[#0A1628] font-bold capitalize">{estimate.trailer}</div>
                    </div>
                    <div>
                      <div className="text-[#4A5A72] font-semibold uppercase tracking-wider text-[10px]">Vehicle</div>
                      <div className="text-[#0A1628] font-bold">{form.vehicleType}</div>
                    </div>
                  </div>
                </div>
                {!estimate.resolved && (
                  <div className="flex items-start gap-2 bg-[#FFF8EC] border border-[#D4A24C]/40 rounded-lg p-3 text-[11px] text-[#0A1628]">
                    <Info className="w-3.5 h-3.5 text-[#D4A24C] mt-0.5 shrink-0" />
                    We could not auto-detect states from your locations, so this uses a national average. Your final quote from our team will be exact.
                  </div>
                )}
                <p className="text-[11px] leading-relaxed text-[#4A5A72]">
                  This is an instant estimate based on distance, trailer type and vehicle class. Continue to lock in a personalized quote from our dispatch team.
                </p>
              </>
            ) : (
              <div className="text-center py-8 text-sm text-[#4A5A72]">
                Tap <strong>Back</strong> and try again — we couldn’t load your estimate.
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Full Name</Label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Jane Doe" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Phone</Label>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(555) 000-0000" className="pl-9 h-11" />
              </div>
            </div>
            <p className="text-[11px] leading-relaxed text-[#4A5A72]">By clicking “Calculate Now” you consent to receive calls, messages and SMS texts from Excelence Inc at the number provided, including automated systems. Reply STOP to opt out.</p>
          </>
        )}

        {/* Nav buttons */}
        <div className="flex gap-2 pt-2">
          {step > 1 && (
            <Button type="button" variant="outline" onClick={back} className="h-11 gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          )}
          {step < 4 ? (
            <Button type="button" onClick={next} disabled={estimateLoading} className="btn-primary flex-1 h-11 gap-1 font-semibold">
              {estimateLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </Button>
          ) : (
            <Button type="submit" disabled={loading} className="btn-accent flex-1 h-11 font-bold">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> SENDING…</>) : 'GET MY QUOTE'}
            </Button>
          )}
        </div>
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#4A5A72] pt-1">
          <Lock className="w-3 h-3" />
          Secured by Excelenceinc.com
        </div>
      </form>
    </div>
  );
};

export default QuoteCalculator;
